import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {IssueType} from '../../api/common-contracts'
import type {GroupMetadata} from '../../api/memex-items/paginated-views'
import {assertNever} from '../../helpers/assert-never'
import {getAllIterations} from '../../helpers/iterations'
import {
  asCustomDateValue,
  asCustomNumberValue,
  asCustomTextValue,
  parseIsoDateStringToDate,
} from '../../helpers/parsing'
import {isGroupMetadataType} from '../../models/column-capabilities'
import type {ColumnModel} from '../../models/column-model'
import {createEmptyGroup} from './helpers'
import type {FieldGrouping} from './types'

/**
 * This method reformats a GroupMetadata object (as received
 * from the server) into a FieldGrouping object by using
 * type narrowing to confirm the provided columnModel.dataType
 * matches the groupMetadata shape.
 * This was introduced to provide backwards-compatible typing
 * during the migration to Projects Without Limits architecture;
 * we expect these types to always be in sync.
 * If we eventually deprecate FieldGrouping in favor of using
 * GroupMetadata directly in components, then this utility can be
 * removed.
 */
export function getGroupingMetadataFromServerGroupValue(
  columnModel: ColumnModel,
  groupValue: string,
  groupMetadata?: GroupMetadata,
): FieldGrouping | undefined {
  if (groupValue === '_noValue') {
    return createEmptyGroup(columnModel)
  }
  if (!isGroupMetadataType(columnModel.dataType)) {
    return undefined
  }
  switch (columnModel.dataType) {
    case MemexColumnDataType.Assignees: {
      if (groupMetadata && 'login' in groupMetadata) {
        return {
          dataType: columnModel.dataType,
          kind: 'group',
          value: [groupMetadata],
        }
      }
      return undefined
    }
    case MemexColumnDataType.Date: {
      // groupValue is an ISO date string and our metadata expects a DateValue
      const dateObject = parseIsoDateStringToDate(groupValue)
      const date = asCustomDateValue({value: dateObject})
      if (!date) {
        return undefined
      }
      return {
        dataType: MemexColumnDataType.Date,
        kind: 'group',
        value: {date, columnId: columnModel.id, isIssueField: columnModel.isIssueField},
      }
    }
    case MemexColumnDataType.Iteration: {
      if (groupMetadata && 'duration' in groupMetadata) {
        return {
          dataType: columnModel.dataType,
          kind: 'group',
          value: {iteration: groupMetadata, columnId: columnModel.id, isIssueField: columnModel.isIssueField},
        }
      }
      // groupValue is an iteration name and our metadata expects an iteration
      const allIterations = getAllIterations(columnModel)
      const iteration = allIterations.find(i => i.title === groupValue)

      if (!iteration) {
        return undefined
      }

      return {
        dataType: MemexColumnDataType.Iteration,
        kind: 'group',
        value: {iteration, columnId: columnModel.id, isIssueField: columnModel.isIssueField},
      }
    }
    case MemexColumnDataType.Labels: {
      if (groupMetadata && 'url' in groupMetadata && 'color' in groupMetadata) {
        return {
          dataType: columnModel.dataType,
          kind: 'group',
          value: groupMetadata,
        }
      }
      return undefined
    }
    case MemexColumnDataType.Milestone: {
      if (groupMetadata && 'url' in groupMetadata && 'title' in groupMetadata && 'repoNameWithOwner' in groupMetadata) {
        return {
          dataType: columnModel.dataType,
          kind: 'group',
          value: groupMetadata,
        }
      }
      return undefined
    }
    case MemexColumnDataType.IssueType: {
      if (groupMetadata && 'name' in groupMetadata && 'id' in groupMetadata) {
        return {
          dataType: columnModel.dataType,
          kind: 'group',
          value: groupMetadata as IssueType,
        }
      }
      return undefined
    }
    case MemexColumnDataType.ParentIssue: {
      if (groupMetadata && 'nwoReference' in groupMetadata && 'id' in groupMetadata) {
        return {
          dataType: columnModel.dataType,
          kind: 'group',
          value: groupMetadata,
        }
      }
      return undefined
    }
    case MemexColumnDataType.Number: {
      // groupValue is a number value as a string and our metadata expects a NumberValue
      const number = asCustomNumberValue({value: Number(groupValue)})
      if (!number) {
        return undefined
      }
      return {
        dataType: MemexColumnDataType.Number,
        kind: 'group',
        value: {number, columnId: columnModel.id, isIssueField: columnModel.isIssueField},
      }
    }
    case MemexColumnDataType.Repository: {
      if (groupMetadata && 'nameWithOwner' in groupMetadata) {
        return {
          dataType: columnModel.dataType,
          kind: 'group',
          value: groupMetadata,
        }
      }
      return undefined
    }
    case MemexColumnDataType.SingleSelect: {
      if (groupMetadata && 'descriptionHtml' in groupMetadata) {
        return {
          dataType: columnModel.dataType,
          kind: 'group',
          value: {option: groupMetadata, columnId: columnModel.id, isIssueField: columnModel.isIssueField},
        }
      }
      // groupValue is an option name and our metadata expects an option
      const options = columnModel.settings.options || []
      const option = options.find(o => o.name === groupValue)
      if (!option) {
        return undefined
      }

      return {
        dataType: MemexColumnDataType.SingleSelect,
        kind: 'group',
        value: {option, columnId: columnModel.id, isIssueField: columnModel.isIssueField},
      }
    }
    case MemexColumnDataType.Text: {
      // groupValue is a string and our metadata expects an EnrichedText
      const text = asCustomTextValue({raw: groupValue, html: groupValue})
      if (!text) {
        return undefined
      }
      return {
        dataType: MemexColumnDataType.Text,
        kind: 'group',
        value: {text, columnId: columnModel.id, isIssueField: columnModel.isIssueField},
      }
    }
    default:
      assertNever(columnModel.dataType)
  }
}
